import { execSync } from "child_process"
import { existsSync, writeFileSync } from "fs"
import { join } from "path"
import type { FeatureInstaller, ProjectConfig } from "../../types/types.js"
import { logger } from "../../utils/logger.js"
import { PackageManagerAdapter } from "../../utils/package-manager.js"

export class LefthookInstaller implements FeatureInstaller {
  name = "Lefthook"

  async install(config: ProjectConfig): Promise<void> {
    logger.info("Setting up Lefthook for Git hooks...")

    const pm = new PackageManagerAdapter(config.packageManager)

    await pm.add(["lefthook"], true, config)
    this.createLefthookConfig(config)
    this.installGitHooks(config)

    logger.success("Lefthook configured successfully")
  }

  private createLefthookConfig(config: ProjectConfig): void {
    const lefthookConfig = this.generateConfig(config)
    const configPath = join(config.projectPath, "lefthook.yml")

    if (config.dryRun) {
      logger.debug("[DRY RUN] Would create Lefthook config")
      return
    }

    writeFileSync(configPath, lefthookConfig)
    logger.debug("Created lefthook.yml")
  }

  private generateConfig(config: ProjectConfig): string {
    const { packageManager, codeQuality } = config
    const pm = packageManager === "npm" ? "npm run" : packageManager
    const lockFile = this.getLockFileName(packageManager)

    let configYaml = `pre-commit:
  commands:`

    // Add format check command based on code quality tool
    if (codeQuality === "biome") {
      configYaml += `
    ${packageManager}-fmt:
      run: |
        # Skip confirmation prompt when Corepack is about to upgrade ${packageManager}.
        CI=1 ${pm} check:format
      stage_fixed: true`
    } else {
      configYaml += `
    ${packageManager}-fmt:
      run: |
        # Skip confirmation prompt when Corepack is about to upgrade ${packageManager}.
        CI=1 ${pm} format:check
      stage_fixed: true`
    }

    configYaml += `
  skip:
    - merge
    - rebase

pre-push:
  parallel: true
  commands:
    ${packageManager}-check:
      run: |
        # Skip confirmation prompts when Corepack is about to upgrade ${packageManager}.
        CI=1 ${pm} check

post-checkout:
  commands:
    ${packageManager}-install:
      # language=sh
      run: |
        OLD_HEAD_SHA="$(git rev-parse HEAD@{1})"
        NEW_HEAD_SHA="$(git rev-parse HEAD)"
        DIFF="$(git diff --name-only "$OLD_HEAD_SHA" "$NEW_HEAD_SHA" -- '${lockFile}')"
        echo "Evaluating changes between the old HEAD commit '$OLD_HEAD_SHA' and the new HEAD commit '$NEW_HEAD_SHA':"
        if echo "$DIFF" | grep -q '${lockFile}'; then
          echo "- Changes detected in '${lockFile}'. Running '${packageManager} install'."
          # Skip confirmation prompts when Corepack is about to upgrade ${packageManager}.
          CI=1 ${packageManager} install
        else
          echo "- No changes detected in '${lockFile}'. Skipping '${packageManager} install'."
        fi

post-rewrite:
  commands:
    ${packageManager}-install:
      use_stdin: true
      # language=sh
      run: |
        while read OLD_SHA NEW_SHA EXTRA; do
          if [ -z "$FIRST_OLD_SHA" ]; then
            FIRST_OLD_SHA="$OLD_SHA"
          fi
        done
        BASE_SHA="$(git rev-parse "$FIRST_OLD_SHA~1")"
        REBASED_SHA="$(git rev-parse HEAD)"
        DIFF="$(git diff --name-only "$BASE_SHA" "$REBASED_SHA" -- '${lockFile}')"
        echo "Evaluating changes between the base commit '$BASE_SHA' and the rebased commit '$REBASED_SHA':"
        if echo "$DIFF" | grep -q '${lockFile}'; then
          echo "- Changes detected in '${lockFile}'. Running '${packageManager} install'."
          # Skip confirmation prompts when Corepack is about to upgrade ${packageManager}.
          CI=1 ${packageManager} install
        else
          echo "- No changes detected in '${lockFile}'. Skipping '${packageManager} install'."
        fi

skip_output:
  - meta
  - summary`

    return configYaml
  }

  private getLockFileName(packageManager: string): string {
    switch (packageManager) {
      case "npm":
        return "package-lock.json"
      case "pnpm":
        return "pnpm-lock.yaml"
      case "yarn":
        return "yarn.lock"
      case "bun":
        return "bun.lockb"
      default:
        return "package-lock.json"
    }
  }

  private installGitHooks(config: ProjectConfig): void {
    if (config.dryRun) {
      logger.debug("[DRY RUN] Would install Git hooks")
      return
    }

    try {
      const gitDir = join(config.projectPath, ".git")

      if (!existsSync(gitDir)) {
        logger.warning(
          "Git not initialized in project directory, skipping hook installation",
        )
        return
      }

      const pm = config.packageManager
      const command =
        pm === "npm"
          ? "npx --prefix . lefthook install"
          : `${pm} exec lefthook install`

      execSync(command, {
        cwd: config.projectPath,
        env: {
          ...process.env,
          // Ensure lefthook only installs in the current directory
          LEFTHOOK_EXCLUDE_TAGS: "meta,summary",
        },
        stdio: "ignore",
      })

      logger.debug("Git hooks installed")
    } catch {
      logger.warning("Could not install Git hooks (Git may not be initialized)")
    }
  }
}
